import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  createVehicleVersion,
  deleteVehicleVersion,
  fetchVehicleModelsPage,
  fetchVehicleVersionsPage,
  updateVehicleVersion,
  type VehicleModelAdmin,
  type VehicleVersionAdmin,
} from "@/lib/catalog/vehicleCatalogAdmin";
import { listBrandRecords, syncBrandsFromApi, type BrandRecord } from "@/lib/catalog/brandsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { slugify } from "@/lib/slugify";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Car, Edit, Eye, Filter, LayoutGrid, Link2, List, Loader2, Plus, Search, Tag, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/components/ui/sonner";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
/** First paint + each search: only one page of models (never bulk-load). */
const MODEL_PICK_PAGE_SIZE = 20;
/** Debounce all model queries (including clearing the box) so backspacing does not spam the API. */
const MODEL_SEARCH_DEBOUNCE_MS = 480;

type VvSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "brand_asc"
  | "sort_asc"
  | "sort_desc";

function vvSortToApi(sort: VvSortBy): string {
  switch (sort) {
    case "oldest":
      return "created_at:asc";
    case "name_desc":
      return "name:desc";
    case "name_asc":
      return "name:asc";
    case "brand_asc":
      return "brand:asc";
    case "sort_desc":
      return "sort_order:desc";
    case "sort_asc":
      return "sort_order:asc";
    case "latest":
    default:
      return "-created_at";
  }
}

function vehicleVersionsDateQuery(
  quickDate: "all" | "today" | "week" | "month",
  filterCreatedDate: Date | undefined,
): { created_on?: string; created_from?: string; created_to?: string } {
  if (filterCreatedDate) {
    return { created_on: format(filterCreatedDate, "yyyy-MM-dd") };
  }
  if (quickDate === "all") return {};
  const now = new Date();
  const end = endOfDay(now).toISOString();
  const start =
    quickDate === "today"
      ? startOfDay(now).toISOString()
      : quickDate === "week"
        ? startOfDay(subDays(now, 6)).toISOString()
        : startOfMonth(now).toISOString();
  return { created_from: start, created_to: end };
}

function formatIsoDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

function modelLabel(m: VehicleModelAdmin, brandName: string): string {
  return `${brandName} — ${m.name}`;
}

/** Allowed catalog years for vehicle versions (inclusive). */
const VV_YEAR_MIN = 1940;
const VV_YEAR_MAX = 2026;
const VV_YEAR_SUGGEST_CAP = 100;

/** Keep only digits, max length 4 (YYYY). */
function fourDigitYearInput(raw: string): string {
  return raw.replace(/\D/g, "").slice(0, 4);
}

/** Years in range whose string form starts with the typed digit prefix (e.g. "20" → 2000…2026). */
function vehicleYearsMatchingPrefix(prefixDigits: string): number[] {
  const d = prefixDigits.replace(/\D/g, "");
  if (d.length === 0) return [];
  const hits: number[] = [];
  for (let y = VV_YEAR_MIN; y <= VV_YEAR_MAX; y++) {
    if (String(y).startsWith(d)) {
      hits.push(y);
      if (hits.length >= VV_YEAR_SUGGEST_CAP) break;
    }
  }
  return hits;
}

/** Plain calendar years only (e.g. 2018–2026, or a single year if one end is set). */
function formatVehicleVersionYearRange(v: {
  start_year?: number | null;
  end_year?: number | null;
}): string {
  const s = v.start_year ?? null;
  const e = v.end_year ?? null;
  if (s == null && e == null) return "";
  if (s != null && e != null) return `${s}–${e}`;
  if (s != null) return String(s);
  return String(e);
}

const VehicleVersions = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canViewAny = can(user, "brand_can_view_any");
  const canView = can(user, "brand_can_view");
  const canCreate = can(user, "brand_can_create");
  const canUpdate = can(user, "brand_can_update");
  const canDelete = can(user, "brand_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [items, setItems] = useState<VehicleVersionAdmin[]>([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [listVersion, setListVersion] = useState(0);
  const [fetching, setFetching] = useState(true);
  /** First API load — same UX as Vehicle Models / Employee Allowance Types. */
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterBrandId, setFilterBrandId] = useState<string>("__all__");
  const [filterModelId, setFilterModelId] = useState<string>("__all__");
  const [filterName, setFilterName] = useState("");
  const [filterSlug, setFilterSlug] = useState("");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [sortBy, setSortBy] = useState<VvSortBy>("latest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleVersionAdmin | null>(null);
  const [formModelId, setFormModelId] = useState("");
  const [formName, setFormName] = useState("");
  const [formPopular, setFormPopular] = useState(false);
  const [formSortOrder, setFormSortOrder] = useState("0");
  const [formStartYear, setFormStartYear] = useState("");
  const [formEndYear, setFormEndYear] = useState("");
  const [formImported, setFormImported] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VehicleVersionAdmin | null>(null);
  const [viewVersion, setViewVersion] = useState<VehicleVersionAdmin | null>(null);

  const [formModelSearch, setFormModelSearch] = useState("");
  const [formModelHits, setFormModelHits] = useState<VehicleModelAdmin[]>([]);
  const [formModelPickerOpen, setFormModelPickerOpen] = useState(false);
  const [formModelSearchLoading, setFormModelSearchLoading] = useState(false);
  const formModelPickerRef = useRef<HTMLDivElement>(null);
  const startYearFieldRef = useRef<HTMLDivElement>(null);
  const endYearFieldRef = useRef<HTMLDivElement>(null);

  const [startYearSuggestOpen, setStartYearSuggestOpen] = useState(false);
  const [endYearSuggestOpen, setEndYearSuggestOpen] = useState(false);

  const [filterModelSearch, setFilterModelSearch] = useState("");
  const [filterModelHits, setFilterModelHits] = useState<VehicleModelAdmin[]>([]);
  const [filterModelPickLabel, setFilterModelPickLabel] = useState("");

  const [formModelPickLabel, setFormModelPickLabel] = useState("");

  const [brandTick, setBrandTick] = useState(0);

  const filterBlankQueryImmediateRef = useRef(false);
  const filterPickerSessionRef = useRef(false);

  const loadBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      await syncBrandsFromApi();
      setBrandTick((t) => t + 1);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[VehicleVersions] brands load failed", error);
      toast.error("Could not load brands.");
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  const filterDepsKey = useMemo(
    () =>
      [
        query,
        sortBy,
        filterBrandId,
        filterModelId,
        filterName,
        filterSlug,
        quickDate,
        filterCreatedDate ? format(filterCreatedDate, "yyyy-MM-dd") : "",
      ].join("|"),
    [query, sortBy, filterBrandId, filterModelId, filterName, filterSlug, quickDate, filterCreatedDate],
  );
  const prevFilterDepsKey = useRef(filterDepsKey);
  useEffect(() => {
    if (prevFilterDepsKey.current !== filterDepsKey) {
      prevFilterDepsKey.current = filterDepsKey;
      setPage(1);
    }
  }, [filterDepsKey]);

  const loadPage = useCallback(async () => {
    setFetching(true);
    setIsForbidden(false);
    try {
      const dateQ = vehicleVersionsDateQuery(quickDate, filterCreatedDate);
      const { data, meta } = await fetchVehicleVersionsPage({
        page,
        per_page: pageSize,
        q: query.trim() || undefined,
        brand_id: filterBrandId !== "__all__" ? filterBrandId : undefined,
        vehicle_model_id: filterModelId !== "__all__" ? filterModelId : undefined,
        name: filterName.trim() || undefined,
        slug: filterSlug.trim() || undefined,
        sort: vvSortToApi(sortBy),
        ...dateQ,
      });
      setItems(data);
      setTotal(meta.total);
      setLastPage(meta.last_page);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[VehicleVersions] load failed", error);
      toast.error("Could not load vehicle versions.");
      setItems([]);
      setTotal(0);
      setLastPage(1);
    } finally {
      setFetching(false);
      setIsBootstrapping(false);
    }
  }, [
    page,
    pageSize,
    query,
    sortBy,
    filterBrandId,
    filterModelId,
    filterName,
    filterSlug,
    quickDate,
    filterCreatedDate,
    listVersion,
  ]);

  useEffect(() => {
    void loadBrands();
  }, [loadBrands]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const reload = useCallback(() => {
    setListVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "inventory.brands.v1") setBrandTick((t) => t + 1);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const brands = useMemo(() => listBrandRecords(), [brandTick]);

  const brandRecordById = useMemo(() => {
    const m = new Map<string, BrandRecord>();
    for (const b of brands) m.set(String(b.id), b);
    return m;
  }, [brands]);

  const brandNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of brands) {
      const id = Number(b.id);
      if (Number.isFinite(id)) m.set(id, b.name);
    }
    return m;
  }, [brands]);

  const appliedFiltersCount =
    (filterBrandId !== "__all__" ? 1 : 0) +
    (filterModelId !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterSlug.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterBrandId("__all__");
    setFilterModelId("__all__");
    setFilterModelSearch("");
    setFilterModelHits([]);
    setFilterModelPickLabel("");
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterSlug("");
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const totalPages = lastPage;

  const startYearHits = useMemo(() => vehicleYearsMatchingPrefix(formStartYear), [formStartYear]);
  const endYearHits = useMemo(() => vehicleYearsMatchingPrefix(formEndYear), [formEndYear]);

  useEffect(() => {
    if (!dialogOpen) {
      setFormModelHits([]);
      setFormModelPickerOpen(false);
      setFormModelSearchLoading(false);
      setStartYearSuggestOpen(false);
      setEndYearSuggestOpen(false);
      return;
    }
    if (formModelId) return;

    const q = formModelSearch.trim();
    if (q.length === 0) {
      setFormModelHits([]);
      setFormModelSearchLoading(false);
      return;
    }

    setFormModelSearchLoading(true);
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const { data } = await fetchVehicleModelsPage({
            page: 1,
            per_page: MODEL_PICK_PAGE_SIZE,
            q: q,
          });
          if (!cancelled) setFormModelHits(data);
        } catch {
          if (!cancelled) setFormModelHits([]);
        } finally {
          if (!cancelled) setFormModelSearchLoading(false);
        }
      })();
    }, MODEL_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      setFormModelSearchLoading(false);
      window.clearTimeout(timer);
    };
  }, [dialogOpen, formModelId, formModelSearch]);

  useEffect(() => {
    if (!dialogOpen || !formModelPickerOpen || formModelId) return;
    const onDoc = (e: MouseEvent) => {
      if (!formModelPickerRef.current?.contains(e.target as Node)) setFormModelPickerOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [dialogOpen, formModelPickerOpen, formModelId]);

  useEffect(() => {
    if (!dialogOpen || (!startYearSuggestOpen && !endYearSuggestOpen)) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (startYearFieldRef.current && !startYearFieldRef.current.contains(t)) {
        setStartYearSuggestOpen(false);
      }
      if (endYearFieldRef.current && !endYearFieldRef.current.contains(t)) {
        setEndYearSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [dialogOpen, startYearSuggestOpen, endYearSuggestOpen]);

  useEffect(() => {
    if (!filtersOpen) {
      filterBlankQueryImmediateRef.current = false;
      filterPickerSessionRef.current = false;
      setFilterModelHits([]);
      return;
    }
    if (filterModelId !== "__all__") return;

    const q = filterModelSearch.trim();
    let cancelled = false;

    const fetchModels = async () => {
      try {
        const { data } = await fetchVehicleModelsPage({
          page: 1,
          per_page: MODEL_PICK_PAGE_SIZE,
          q: q.length > 0 ? q : undefined,
          brand_id: filterBrandId !== "__all__" ? filterBrandId : undefined,
        });
        if (!cancelled) {
          setFilterModelHits(data);
          if (q.length === 0) filterPickerSessionRef.current = true;
        }
      } catch {
        if (!cancelled) setFilterModelHits([]);
      }
    };

    const fastFromUi = filterBlankQueryImmediateRef.current;
    if (fastFromUi) filterBlankQueryImmediateRef.current = false;
    const allowFastBlank = q.length === 0 && (fastFromUi || !filterPickerSessionRef.current);
    const delay = allowFastBlank ? 0 : MODEL_SEARCH_DEBOUNCE_MS;

    const timer = window.setTimeout(() => {
      void fetchModels();
    }, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [filtersOpen, filterModelId, filterModelSearch, filterBrandId]);

  const openCreate = () => {
    setEditing(null);
    setFormModelId("");
    setFormModelPickLabel("");
    setFormModelSearch("");
    setFormModelHits([]);
    setFormName("");
    setFormPopular(false);
    setFormSortOrder("0");
    setFormStartYear("");
    setFormEndYear("");
    setFormImported(false);
    setFormModelPickerOpen(true);
    setDialogOpen(true);
  };

  const openEdit = (r: VehicleVersionAdmin) => {
    setEditing(r);
    setFormModelId(String(r.vehicle_model_id));
    const bn = r.brand_name || brandNameById.get(r.brand_id ?? 0) || "";
    const mn = r.vehicle_model_name || "";
    setFormModelPickLabel([bn, mn].filter(Boolean).join(" — "));
    setFormModelSearch("");
    setFormModelHits([]);
    setFormModelPickerOpen(false);
    setFormName(r.name);
    setFormPopular(r.is_popular);
    setFormSortOrder(String(r.sort_order ?? 0));
    setFormStartYear(r.start_year != null ? String(r.start_year) : "");
    setFormEndYear(r.end_year != null ? String(r.end_year) : "");
    setFormImported(Boolean(r.is_imported));
    setDialogOpen(true);
  };

  const save = async () => {
    if (saving) return;
    const modelId = Number.parseInt(String(formModelId), 10);
    if (!Number.isFinite(modelId) || modelId < 1) {
      toast.error("Select a vehicle model.");
      return;
    }
    if (!formName.trim()) {
      toast.error("Name is required.");
      return;
    }
    const slug = slugify(formName).trim();
    if (!slug) {
      toast.error("Name must include letters or numbers.");
      return;
    }
    const sortOrder = Number(formSortOrder);
    if (!Number.isFinite(sortOrder) || sortOrder < 0) {
      toast.error("Sort order must be a non-negative number.");
      return;
    }
    let startYear: number | null = null;
    const startTrim = formStartYear.trim();
    if (startTrim !== "") {
      if (!/^\d{4}$/.test(startTrim)) {
        toast.error("Start year must be exactly four digits (e.g. 2018), or left blank.");
        return;
      }
      const n = Number.parseInt(startTrim, 10);
      if (!Number.isFinite(n) || n < VV_YEAR_MIN || n > VV_YEAR_MAX) {
        toast.error(`Start year must be between ${VV_YEAR_MIN} and ${VV_YEAR_MAX}, or left blank.`);
        return;
      }
      startYear = n;
    }
    let endYear: number | null = null;
    const endTrim = formEndYear.trim();
    if (endTrim !== "") {
      if (!/^\d{4}$/.test(endTrim)) {
        toast.error("End year must be exactly four digits (e.g. 2026), or left blank.");
        return;
      }
      const n = Number.parseInt(endTrim, 10);
      if (!Number.isFinite(n) || n < VV_YEAR_MIN || n > VV_YEAR_MAX) {
        toast.error(`End year must be between ${VV_YEAR_MIN} and ${VV_YEAR_MAX}, or left blank.`);
        return;
      }
      endYear = n;
    }
    if (startYear != null && endYear != null && endYear < startYear) {
      toast.error("End year must be greater than or equal to start year.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateVehicleVersion(editing.id, {
          vehicle_model_id: modelId,
          name: formName.trim(),
          slug,
          is_popular: formPopular,
          sort_order: sortOrder,
          start_year: startYear,
          end_year: endYear,
          is_imported: formImported,
        });
        toast.success("Vehicle version updated.");
      } else {
        await createVehicleVersion({
          vehicle_model_id: modelId,
          name: formName.trim(),
          slug,
          is_popular: formPopular,
          sort_order: sortOrder,
          start_year: startYear,
          end_year: endYear,
          is_imported: formImported,
        });
        toast.success("Vehicle version created.");
        setPage(1);
      }
      setDialogOpen(false);
      reload();
    } catch (error) {
      console.error("[VehicleVersions] save failed", error);
      if (isAxiosError(error) && error.response?.data) {
        const data = error.response.data as { message?: string; errors?: Record<string, string[]> };
        const firstField = data.errors ? Object.values(data.errors).flat()[0] : undefined;
        toast.error(firstField || (data.message ? String(data.message) : "Save failed."));
      } else {
        toast.error("Save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await deleteVehicleVersion(deleteTarget.id);
      toast.success("Vehicle version deleted.");
      setDeleteTarget(null);
      reload();
    } catch (error) {
      console.error("[VehicleVersions] delete failed", error);
      toast.error("Delete failed.");
    }
  };

  if (!canViewAny) {
    return (
      <PermissionDenied
        title="Catalog access required"
        message="You need permission to view brands (brand_can_view_any) to manage versions."
      />
    );
  }

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Versions access required"
        message="You don’t have permission to load vehicle versions. Ask an administrator to grant brand catalog access."
      />
    );
  }

  return (
    <Layout title="Versions">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Versions</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Trims and packages under each model — optional step in the product name picker.
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate} disabled={brandsLoading}>
              <Plus className="h-4 w-4" />
              Add version
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, model, brand…"
                className="glass-input h-10 pl-9"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet
              open={filtersOpen}
              onOpenChange={(o) => {
                setFiltersOpen(o);
                if (o) filterBlankQueryImmediateRef.current = true;
              }}
            >
              <SheetTrigger asChild>
                <Button variant="glass" className="shrink-0 gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter versions</SheetTitle>
                  <SheetDescription>Refine by brand, model, name, slug, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Version</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Brand</Label>
                      <Select
                        value={filterBrandId}
                        onValueChange={(v) => {
                          setFilterBrandId(v);
                          setFilterModelId("__all__");
                          setFilterModelSearch("");
                          setFilterModelHits([]);
                          setFilterModelPickLabel("");
                          filterBlankQueryImmediateRef.current = true;
                        }}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All brands" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All brands</SelectItem>
                          {brands.map((b) => (
                            <SelectItem key={b.id} value={String(b.id)}>
                              {b.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Vehicle model</Label>
                      <p className="text-xs text-muted-foreground">
                        Pick from the first {MODEL_PICK_PAGE_SIZE} models
                        {filterBrandId !== "__all__" ? " for the brand above" : ""}, or type to search (also loads up to{" "}
                        {MODEL_PICK_PAGE_SIZE} matches).
                      </p>
                      {filterModelId !== "__all__" ? (
                        <div className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                          <span className="min-w-0 truncate text-sm text-foreground">
                            {filterModelPickLabel || `Model #${filterModelId}`}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 shrink-0 px-2 text-xs"
                            onClick={() => {
                              setFilterModelId("__all__");
                              setFilterModelPickLabel("");
                              setFilterModelSearch("");
                              setFilterModelHits([]);
                              filterBlankQueryImmediateRef.current = true;
                            }}
                          >
                            Clear
                          </Button>
                        </div>
                      ) : null}
                      <div className="relative">
                        <Input
                          className="glass-input h-10"
                          value={filterModelSearch}
                          onChange={(e) => setFilterModelSearch(e.target.value)}
                          placeholder="Search models…"
                          autoComplete="off"
                          disabled={filterModelId !== "__all__"}
                        />
                      </div>
                      {filterModelId === "__all__" && filterModelHits.length > 0 ? (
                        <ul
                          role="listbox"
                          className="max-h-48 overflow-y-auto rounded-md border border-border bg-popover text-sm shadow-md"
                        >
                          {filterModelHits.map((m) => {
                            const bName = m.brand_name || brandNameById.get(m.brand_id) || String(m.brand_id);
                            return (
                              <li key={m.id}>
                                <button
                                  type="button"
                                  role="option"
                                  className="flex w-full items-center px-3 py-2 text-left hover:bg-muted/60"
                                  onClick={() => {
                                    setFilterModelId(String(m.id));
                                    setFilterModelPickLabel(modelLabel(m, bName));
                                    setFilterModelSearch("");
                                    setFilterModelHits([]);
                                  }}
                                >
                                  {modelLabel(m, bName)}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Version name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. 2.7 G"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Slug contains</Label>
                      <Input
                        className="glass-input h-10 font-mono text-sm"
                        value={filterSlug}
                        onChange={(e) => setFilterSlug(e.target.value)}
                        placeholder="e.g. 2-7-g"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 shrink-0 overflow-hidden rounded-lg border border-border bg-background">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="vehicle-versions-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
              options={[...PAGE_SIZE_OPTIONS]}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as VvSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="brand_asc">Brand, then name</SelectItem>
                  <SelectItem value="sort_asc">Sort order: low to high</SelectItem>
                  <SelectItem value="sort_desc">Sort order: high to low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden items-center justify-between gap-3 md:flex">
            <GridPerPageSelect
              id="vehicle-versions-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
              options={[...PAGE_SIZE_OPTIONS]}
            />
            <div className="flex min-w-0 items-center justify-end gap-3">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as VvSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="brand_asc">Brand, then name</SelectItem>
                    <SelectItem value="sort_asc">Sort order: low to high</SelectItem>
                    <SelectItem value="sort_desc">Sort order: high to low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 shrink-0 overflow-hidden rounded-lg border border-border bg-background">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <>
            {isBootstrapping ? (
              <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading vehicle versions…
              </div>
            ) : null}

            {!isBootstrapping && total === 0 && !fetching ? (
              <div className="glass-card p-6 text-sm text-muted-foreground">
                {query.trim() ||
                filterBrandId !== "__all__" ||
                filterModelId !== "__all__" ||
                filterName.trim() ||
                filterSlug.trim() ||
                filterCreatedDate ||
                quickDate !== "all"
                  ? "No vehicle versions match your filters."
                  : canCreate
                    ? "No versions yet. Click “Add version” to create one."
                    : "No versions yet."}
              </div>
            ) : null}

            {!isBootstrapping && total > 0 && viewMode === "grid" ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {items.map((r, i) => {
                  const brandId = r.brand_id ?? undefined;
                  const br = brandId != null ? brandRecordById.get(String(brandId)) : undefined;
                  const thumb = br?.logoDataUrl;
                  const brandLabel =
                    r.brand_name ||
                    (r.brand_id != null ? brandNameById.get(r.brand_id) : undefined) ||
                    (r.brand_id != null ? String(r.brand_id) : "—");
                  const mname = r.vehicle_model_name || String(r.vehicle_model_id);
                  const yearRange = formatVehicleVersionYearRange(r);
                  const origin = r.is_imported ? "Imported" : "Local";
                  return (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <div className="rounded-lg border border-border bg-card text-card-foreground overflow-hidden shadow-sm">
                        <div className="h-1 w-full bg-primary" aria-hidden />
                        <div className="p-4 pt-5 space-y-4">
                          <div className="flex gap-3 items-start">
                            {thumb ? (
                              <img
                                src={thumb}
                                alt=""
                                className="h-12 w-12 rounded-lg object-contain border border-border shrink-0 bg-muted/30 p-0.5"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0 border border-border/50">
                                <Car className="w-6 h-6 text-muted-foreground/60" />
                              </div>
                            )}
                            <h3 className="text-lg font-bold uppercase tracking-tight text-foreground leading-snug break-words flex-1 min-w-0">
                              {r.name}
                            </h3>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                            <div className="flex gap-2.5 items-start min-w-0">
                              <Link2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" strokeWidth={2} />
                              <div className="min-w-0">
                                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Slug</div>
                                <div className="font-semibold text-sm text-foreground truncate font-mono" title={r.slug}>
                                  {r.slug}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2.5 items-start min-w-0">
                              <Tag className="w-4 h-4 shrink-0 mt-0.5 text-red-600" strokeWidth={2} />
                              <div className="min-w-0">
                                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Brand</div>
                                <div className="font-semibold text-sm text-foreground truncate" title={brandLabel}>
                                  {brandLabel}
                                </div>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {yearRange ? (
                              <>
                                <span className="font-medium tabular-nums text-foreground/90">{yearRange}</span>
                                {" · "}
                              </>
                            ) : null}
                            {origin} · Model: <span className="font-medium text-foreground/90">{mname}</span>
                            {r.is_popular ? " · Popular" : ""} · Sort order {r.sort_order}
                          </p>
                          <div className="border-t border-border pt-3">
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              Updated: {formatCatalogAuditDate(r.updated_at ?? r.created_at)}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {canView ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 min-w-[5.5rem] gap-2 h-9 text-xs font-medium"
                                onClick={() => setViewVersion(r)}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View
                              </Button>
                            ) : null}
                            {canUpdate ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 min-w-[5.5rem] gap-2 h-9 text-xs font-medium"
                                onClick={() => openEdit(r)}
                              >
                                <Edit className="w-3.5 h-3.5" />
                                Edit
                              </Button>
                            ) : null}
                            {canDelete ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-9 w-9 shrink-0 p-0 border-destructive/60 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setDeleteTarget(r)}
                                aria-label="Delete version"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : null}

            {!isBootstrapping && total > 0 && viewMode === "list" ? (
              <div className="space-y-3">
                {items.map((r, i) => {
                  const brandId = r.brand_id ?? undefined;
                  const br = brandId != null ? brandRecordById.get(String(brandId)) : undefined;
                  const thumb = br?.logoDataUrl;
                  const brandLabel =
                    r.brand_name ||
                    (r.brand_id != null ? brandNameById.get(r.brand_id) : undefined) ||
                    (r.brand_id != null ? String(r.brand_id) : "—");
                  const mname = r.vehicle_model_name || String(r.vehicle_model_id);
                  const yearRange = formatVehicleVersionYearRange(r);
                  const origin = r.is_imported ? "Imported" : "Local";
                  return (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className="glass-card p-3"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                          {thumb ? (
                            <img src={thumb} alt="" className="h-full w-full object-contain p-1" />
                          ) : (
                            <Car className="w-8 h-8 text-muted-foreground/40" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm font-semibold text-foreground truncate">{r.name}</h3>
                            <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-600 font-mono truncate max-w-[140px]">
                              {r.slug}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {brandLabel} · {mname}
                            {yearRange ? (
                              <>
                                {" "}
                                · <span className="tabular-nums text-foreground/90">{yearRange}</span>
                              </>
                            ) : null}
                            {" "}
                            · {origin}
                            {r.is_popular ? " · Popular" : ""} · Sort {r.sort_order} · Updated{" "}
                            {formatCatalogAuditDate(r.updated_at ?? r.created_at)}
                          </p>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                          {canView ? (
                            <Button variant="glass" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setViewVersion(r)}>
                              <Eye className="h-3 w-3" /> View
                            </Button>
                          ) : null}
                          {canUpdate ? (
                            <Button variant="glass" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => openEdit(r)}>
                              <Edit className="h-3 w-3" /> Edit
                            </Button>
                          ) : null}
                          {canDelete ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteTarget(r)}
                              aria-label="Delete version"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : null}

            {!isBootstrapping && total > 0 ? (
              <GridPaginationFooter
                page={page}
                totalPages={totalPages}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                totalCount={total}
                onPageChange={setPage}
              />
            ) : null}
        </>

        <Dialog open={!!viewVersion} onOpenChange={(o) => !o && setViewVersion(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Version details</DialogTitle>
            </DialogHeader>
            {viewVersion ? (() => {
              const vBrandId = viewVersion.brand_id ?? undefined;
              const vbLogo = vBrandId != null ? brandRecordById.get(String(vBrandId))?.logoDataUrl : undefined;
              const vbName =
                viewVersion.brand_name ||
                (vBrandId != null ? brandNameById.get(vBrandId) : undefined) ||
                (vBrandId != null ? String(vBrandId) : "—");
              const vmName = viewVersion.vehicle_model_name || String(viewVersion.vehicle_model_id);
              return (
                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                      {vbLogo ? (
                        <img src={vbLogo} alt="" className="h-full w-full object-contain p-1" />
                      ) : (
                        <Tag className="h-8 w-8 text-muted-foreground/60" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-base font-semibold text-foreground">{viewVersion.name}</p>
                      <p className="mt-0.5 font-mono text-xs text-muted-foreground">{viewVersion.slug}</p>
                    </div>
                  </div>
                  <dl className="grid grid-cols-1 gap-3">
                    <div>
                      <dt className="text-xs text-muted-foreground">Vehicle model</dt>
                      <dd className="text-muted-foreground">
                        {vbName} — {vmName}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Start year</dt>
                      <dd className="text-muted-foreground tabular-nums">{viewVersion.start_year ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">End year</dt>
                      <dd className="text-muted-foreground tabular-nums">{viewVersion.end_year ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Imported</dt>
                      <dd className="text-muted-foreground">{viewVersion.is_imported ? "Yes" : "No"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Popular</dt>
                      <dd className="text-muted-foreground">{viewVersion.is_popular ? "Yes" : "No"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Sort order</dt>
                      <dd className="text-muted-foreground">{viewVersion.sort_order}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Created</dt>
                      <dd className="text-muted-foreground">{formatIsoDate(viewVersion.created_at)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Last updated</dt>
                      <dd className="text-muted-foreground">{formatCatalogAuditDate(viewVersion.updated_at ?? viewVersion.created_at)}</dd>
                    </div>
                  </dl>
                </div>
              );
            })() : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewVersion(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog modal={false} open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit vehicle version" : "Add vehicle version"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Vehicle model *</Label>
                <div ref={formModelPickerRef} className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    className={cn(
                      "glass-input h-9 w-full pl-9 text-sm",
                      formModelId && "cursor-pointer bg-muted/25 text-foreground",
                    )}
                    value={formModelId ? (formModelPickLabel || `Model #${formModelId}`) : formModelSearch}
                    readOnly={Boolean(formModelId)}
                    title={formModelId ? "Click the field to search for a different model" : undefined}
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-expanded={formModelPickerOpen}
                    placeholder={formModelId ? "" : "Search models.."}
                    onChange={(e) => {
                      if (formModelId) return;
                      setFormModelSearch(e.target.value);
                      setFormModelPickerOpen(true);
                    }}
                    onClick={() => {
                      if (!formModelId) return;
                      setFormModelId("");
                      setFormModelPickLabel("");
                      setFormModelSearch("");
                      setFormModelHits([]);
                      setFormModelPickerOpen(true);
                    }}
                    onFocus={() => {
                      if (formModelId) return;
                      setFormModelPickerOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setFormModelPickerOpen(false);
                      if (
                        formModelId &&
                        (e.key === "Backspace" || e.key === "Delete")
                      ) {
                        setFormModelId("");
                        setFormModelPickLabel("");
                        setFormModelSearch("");
                        setFormModelHits([]);
                        setFormModelPickerOpen(true);
                      }
                    }}
                  />
                  {formModelPickerOpen ? (
                    <div
                      role="listbox"
                      className="absolute left-0 right-0 top-full z-[200] mt-1 max-h-[min(24rem,70vh)] overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
                    >
                      {formModelSearchLoading ? (
                        <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
                          Searching…
                        </div>
                      ) : formModelSearch.trim().length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                          Start typing to search models.
                        </div>
                      ) : formModelHits.length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-muted-foreground">No vehicle models found.</div>
                      ) : (
                        <ul className="p-1">
                          {formModelHits.map((m) => {
                            const br = brandRecordById.get(String(m.brand_id));
                            const thumb = br?.logoDataUrl;
                            const bName = m.brand_name || brandNameById.get(m.brand_id) || String(m.brand_id);
                            return (
                              <li key={m.id}>
                                <button
                                  type="button"
                                  role="option"
                                  className="flex w-full items-center gap-3 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setFormModelId(String(m.id));
                                    setFormModelPickLabel(modelLabel(m, bName));
                                    setFormModelSearch("");
                                    setFormModelHits([]);
                                    setFormModelPickerOpen(false);
                                  }}
                                >
                                  <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted">
                                    {thumb ? (
                                      <img src={thumb} alt="" className="h-full w-full object-contain p-0.5" />
                                    ) : (
                                      <div className="flex h-full w-full items-center justify-center">
                                        <Car className="h-6 w-6 text-muted-foreground/50" aria-hidden />
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate font-medium text-foreground">{m.name}</p>
                                    <p className="truncate text-xs text-muted-foreground">{bName}</p>
                                    <p className="truncate font-mono text-[11px] text-muted-foreground/90">{m.slug}</p>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="space-y-2 min-w-0">
                <Label className="text-xs text-muted-foreground">Version name *</Label>
                <Input
                  className="glass-input h-10"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div ref={startYearFieldRef} className="relative min-w-0 space-y-2">
                  <Label className="text-xs text-muted-foreground">Start year</Label>
                  <Input
                    className="glass-input h-10 w-full font-mono text-sm tabular-nums tracking-wide"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={4}
                    value={formStartYear}
                    onChange={(e) => {
                      const v = fourDigitYearInput(e.target.value);
                      setFormStartYear(v);
                      const hit = vehicleYearsMatchingPrefix(v);
                      setStartYearSuggestOpen(v.length > 0 && v.length < 4 && hit.length > 0);
                    }}
                    onFocus={() => {
                      setEndYearSuggestOpen(false);
                      const hit = vehicleYearsMatchingPrefix(formStartYear);
                      setStartYearSuggestOpen(
                        formStartYear.length > 0 && formStartYear.length < 4 && hit.length > 0,
                      );
                    }}
                  />
                  {startYearSuggestOpen &&
                  formStartYear.length > 0 &&
                  formStartYear.length < 4 &&
                  startYearHits.length > 0 ? (
                    <ul
                      role="listbox"
                      className="absolute left-0 right-0 top-full z-[200] mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
                    >
                      {startYearHits.map((y) => (
                        <li key={y}>
                          <button
                            type="button"
                            role="option"
                            className="w-full px-3 py-1.5 text-left text-sm tabular-nums tracking-wide hover:bg-accent hover:text-accent-foreground"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setFormStartYear(String(y));
                              setStartYearSuggestOpen(false);
                            }}
                          >
                            {y}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                <div ref={endYearFieldRef} className="relative min-w-0 space-y-2">
                  <Label className="text-xs text-muted-foreground">End year</Label>
                  <Input
                    className="glass-input h-10 w-full font-mono text-sm tabular-nums tracking-wide"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={4}
                    value={formEndYear}
                    onChange={(e) => {
                      const v = fourDigitYearInput(e.target.value);
                      setFormEndYear(v);
                      const hit = vehicleYearsMatchingPrefix(v);
                      setEndYearSuggestOpen(v.length > 0 && v.length < 4 && hit.length > 0);
                    }}
                    onFocus={() => {
                      setStartYearSuggestOpen(false);
                      const hit = vehicleYearsMatchingPrefix(formEndYear);
                      setEndYearSuggestOpen(
                        formEndYear.length > 0 && formEndYear.length < 4 && hit.length > 0,
                      );
                    }}
                  />
                  {endYearSuggestOpen &&
                  formEndYear.length > 0 &&
                  formEndYear.length < 4 &&
                  endYearHits.length > 0 ? (
                    <ul
                      role="listbox"
                      className="absolute left-0 right-0 top-full z-[200] mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
                    >
                      {endYearHits.map((y) => (
                        <li key={y}>
                          <button
                            type="button"
                            role="option"
                            className="w-full px-3 py-1.5 text-left text-sm tabular-nums tracking-wide hover:bg-accent hover:text-accent-foreground"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setFormEndYear(String(y));
                              setEndYearSuggestOpen(false);
                            }}
                          >
                            {y}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox id="vv-imported" checked={formImported} onCheckedChange={(c) => setFormImported(Boolean(c))} />
                  <Label htmlFor="vv-imported" className="text-sm text-muted-foreground cursor-pointer">
                    Imported
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="vv-pop" checked={formPopular} onCheckedChange={(c) => setFormPopular(Boolean(c))} />
                  <Label htmlFor="vv-pop" className="text-sm text-muted-foreground cursor-pointer">
                    Popular
                  </Label>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Sort order</Label>
                <Input
                  className="glass-input h-10"
                  type="number"
                  min={0}
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" variant="premium" disabled={saving} onClick={() => void save()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={deleteTarget != null}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this vehicle version?"
          description={
            deleteTarget ? (
              <span>
                Remove <strong className="text-foreground">{deleteTarget.name}</strong> from the catalog?
              </span>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void remove()}
        />
      </div>
    </Layout>
  );
};

export default VehicleVersions;
